// prettier-ignore
import { RIASRecordType, AssessmentState, AssessmentData, SubscriptionState } from "~/lib";
import { getByUser, getProfile, updateDB } from "~/_server/dynamoDB";
import { sendEmail } from "~/_server";
import { SEND_TO_ADMINS } from "~/lib/constants";

// prettier-ignore
export const markAssessmentCompleted = async ( userId: number, scheme: string ) => {
	"use server";

	const subs = await getByUser( RIASRecordType.SUBSCRIPTION, userId, -1, null ).then( res => res?.items ?? [] );

	await Promise.all( subs.map( async s => {
		if ( s.status == SubscriptionState.ASSESSING ) {
			const schemeKeys = scheme === "s6Domestic" ? ["section-6-domestic"] : ["section-6-non-domestic","section-6-non-domestic-advanced"];

			if ( schemeKeys.includes( s.scheme ) ) {
				return await updateDB( s.pk, s.sk, { status: SubscriptionState.PRACTICAL } );	
			}
		}
	} ) );

	const user = await getProfile(userId);

	await sendEmail( {
		templateName: "exams-passed",
		props: { user: { name: user.firstName }, schemeGroup: scheme === "s6Domestic" ? "domestic" : "non-domestic" },
		to: user.email,
		bcc: SEND_TO_ADMINS,
	} );
}

// prettier-ignore
export const getHasCompletedAssesment = async (	userId: number,	scheme: string ) => {
	"use server";
	
	const results = await getAssessmentStatus( userId );

	const schemeStatus = results?.[scheme === "s6Domestic" ? "Domestic" : "NonDomestic"];	

	return schemeStatus.recent == 6;
};

export const getAssessmentStatus = async (userId: number) => {
	"use server";

	const modules = await import("~/data/assessmentModules.json").then(
		res => res.default as AssessmentData,
	);

	const nonDomesticKeys = new Set<string>(
		modules.s6NonDomestic.map(obj => Object.keys(obj)[0]),
	);
	const domesticKeys = new Set<string>(
		modules.s6Domestic.map(obj => Object.keys(obj)[0]),
	);

	type AssessmentItem = {
		pk: string;
		sk: string;
		module: string;
		status: AssessmentState;
		created: number;
		examDate: number;
		answers: Record<string, string>;
	};

	const assessments = await getByUser(
		RIASRecordType.ASSESSMENT,
		userId,
		-1,
		null,
	).then(res => (res?.items ?? []) as AssessmentItem[]);

	const twoYearsAgo = Date.now() / 1000 - 2 * 365.25 * 24 * 60 * 60;

	type Counts = { total: number; recent: number; attempted: number };

	const countPassed = (moduleKeys: Set<string>): Counts => {
		const uniqueCounts = assessments.reduce(
			(acc, a) => {
				if (moduleKeys.has(a.module)) {
					acc.attemptedSet.add(a.module);

					if (a.status === AssessmentState.PASSED) {
						acc.totalSet.add(a.module);

						if (Number(a.examDate) > twoYearsAgo) {
							acc.recentSet.add(a.module);
						}
					}
				}
				return acc;
			},
			{
				totalSet: new Set<string>(),
				recentSet: new Set<string>(),
				attemptedSet: new Set<string>(),
			},
		);

		return {
			total: uniqueCounts.totalSet.size,
			recent: uniqueCounts.recentSet.size,
			attempted: uniqueCounts.attemptedSet.size,
		};
	};

	return {
		Domestic: countPassed(domesticKeys),
		NonDomestic: countPassed(nonDomesticKeys),
	};
};
